import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Asset } from '../../../../model/asset';
import { AssetsService } from '../../../../services/assets.service';
import { AssetMeasurement } from '../../../../model/asset-measurement';
import { MeasuresService } from '../../../../services/measures.service';
import { translate } from '@ngneat/transloco';
import { TimezoneService } from '../../../../services/timezone.service';
import {ConversorsService} from '../../../../services/conversors.service';
import { finalize } from 'rxjs/operators';
import { GenericGraphComponent } from '../../../shared/generic-graph/generic-graph.component';

type ModeOption = {label: string, value: 'chart' | 'table'};
type MeasurementUnit = {origin: string, target: string, name: string};
type SelectMeasurementUnit = {origin: string, target: string, name: string};
type SelectItemGr = {measure: string, label: string, value: string, items: MeasurementUnit[]};

@Component({
  selector: 'app-asset-measures',
  templateUrl: './asset-measures.component.html',
  styleUrls: ['./asset-measures.component.scss']
})
export class AssetMeasuresComponent implements OnInit {

  @Input()
  public asset: Asset;

  @ViewChild('measuresChart')
  public measuresChart: GenericGraphComponent;

  public assetMeasurements: AssetMeasurement[] = [];
  public selectedAssetMeasurements: any[];
  public deviceMeasurements: any[] = [];
  public selectedDeviceMeasurements: any[];
  public deviceMeasurementsItemList: any[];
  public graphAxis: any[];

  public rangeDates: Date[];
  public tableMeasures: any;
  public measures: any;

  public modeOptions: ModeOption[];
  public selectedModeOption: ModeOption;

  public selectedAssetMeasurementUnit: MeasurementUnit;
  public measurementUnitAsset: boolean;
  public groupedAssetMeasurementUnit: SelectItemGr[];
  public selectedMeasurements: any[];
  public selectedAssetMeasurementsUnit: SelectMeasurementUnit[];

  public selectedDeviceMeasurementUnit: MeasurementUnit;
  public measurementUnitDevice: boolean;
  public groupedDeviceMeasurementUnit: SelectItemGr[];
  public selectedDeviceMeasurementsUnit: SelectMeasurementUnit[];

  public actionsMeasure: any[];
  public displayActionsDialog: boolean;

  public convert = this.conversorsService.convert;

  public loadingMeasurements: boolean;
  public rows = 10;
  public first = 0;
  public totalMeasures: number;
  public exportVisible: boolean;

  public exportChart;

  constructor(private assetsService: AssetsService,
              private timezoneService: TimezoneService,
              private measuresService: MeasuresService,
              private conversorsService: ConversorsService) {
    const lastWeek = new Date();
    lastWeek.setHours(lastWeek.getHours() - 24 * 7);
    this.rangeDates = [lastWeek, new Date()];
    this.buildModeOptions();
    this.selectedAssetMeasurementsUnit = [];
    this.selectedDeviceMeasurementsUnit = [];

  }

  ngOnInit(): void {
    this.loadMeasurements();
    this.actionsMeasure = [
      {label: translate('model.conversion'), icon: 'pi pi-cog', command: () => this.showConversions()},
      {label: translate('common.exportXls'), icon: 'pi pi-file-excel', command: () => this.showExportToXls()},
      {label: translate('common.downloadPng'), icon: 'pi pi-download', command: () => this.downloadChart()}
    ];
  }

  showConversions(): void {
    this.displayActionsDialog = true;
    this.measuresChart.resize();
  }

  hideConversions(): void {
    this.displayActionsDialog = false;
    this.measuresChart.resize();
  }

  showExportToXls(): void {
    this.exportVisible = true;
    this.selectedModeOption = this.modeOptions[1];
  }

  downloadChart(): void {
    if (this.selectedModeOption.value === 'table') {
      this.selectedModeOption = this.modeOptions[0];
    }
    try {
      setTimeout(() => {
        if (this.measuresChart.chart.axes !== undefined) {
          this.exportChart = this.measuresChart.chart;
        }
        this.measuresChart.download(this.exportChart);
      });
    } catch (ignored) {}
  }

  private buildModeOptions(): void {
    this.modeOptions = [
      {label: translate('misc.mode.chart'), value: 'chart'},
      {label: translate('misc.mode.table'), value: 'table'}
    ];
    this.selectedModeOption = this.modeOptions[0];
  }

  private loadMeasurements(): void {
    this.loadingMeasurements = true;
    this.assetsService.getAssetMeasurementsOfAsset(this.asset.uuid).subscribe(response => {

      response.assetMeasurements.forEach(measurement => {
        measurement.nameDesc =  measurement.name + measurement.alias +  measurement.description;
        if (measurement.description !== undefined && measurement.description.trim() !== '') {
          measurement.name = measurement.description;
        } else if (measurement.alias !== undefined && measurement.alias.trim() !== '') {
          measurement.name = measurement.alias;
        }
      });

      this.assetMeasurements = response.assetMeasurements
        .sort((a, b) => a.name > b.name ? 1 : -1);
    });
    this.assetsService.getAssetDeviceMeasurements(this.asset.uuid)
      .pipe(finalize(() => this.loadingMeasurements = false))
      .subscribe(response => {
      this.deviceMeasurements = response.deviceMeasurements;
      this.deviceMeasurementsItemList = [];
      const items = new Map<string, any[]>();
      response.deviceMeasurements.forEach(measurement => {
        const obj = items.get(measurement.deviceSerialNumber);

        measurement.nameDesc =  measurement.name + measurement.alias +  measurement.description;
        if (measurement.description !== undefined && measurement.description.trim() !== '') {
          measurement.name = measurement.description;
        } else if (measurement.alias !== undefined && measurement.alias.trim() !== '') {
          measurement.name = measurement.alias;
        }

        if (!obj) {
          items.set(measurement.deviceSerialNumber, [measurement]);
        } else {
          obj.push(measurement);
        }
      });
      items.forEach((value, key) => {
        this.deviceMeasurementsItemList.push({
          name: key,
          uuid: '',
          items: value.sort((a, b) => a.name > b.name ? 1 : -1)
        });
      });
    });
  }

  private loadAssetMeasurementUnits(): void {
    const units = this.convert().possibilities();
    this.groupedAssetMeasurementUnit = [];
    this.measurementUnitAsset = false;
    this.assetMeasurements.forEach(measurement => {
      if (this.selectedAssetMeasurements.includes(measurement.uuid)) {
        if (measurement.measurementType.unit) {
          const originUnit = units.find(measure => measure === measurement.measurementType.unit);
          const items: MeasurementUnit[] = [];

          this.convert().from(originUnit).possibilities().forEach(poss => {
            items.push({
              origin: originUnit,
              target: poss,
              name: `${poss} (${this.convert().describe(poss).plural})`
            });
          });
          this.groupedAssetMeasurementUnit.push({
            measure: measurement.name,
            label: originUnit,
            value: originUnit,
            items: items
          });
          this.selectedAssetMeasurementsUnit[measurement.name] = {
            origin: originUnit,
            target: originUnit,
            name: `${originUnit} (${this.convert().describe(originUnit).plural})`
          };
        }
      }
    });
  }

  private loadDeviceMeasurementUnits(): void {
    const units = this.convert().possibilities();
    this.groupedDeviceMeasurementUnit = [];
    this.measurementUnitDevice = false;
    this.deviceMeasurements.forEach(measurement => {
      if (this.selectedDeviceMeasurements.includes(measurement.uuid)) {
        if (measurement.unit) {
          const originUnit = units.find(measure => measure === measurement.unit);
          const items: MeasurementUnit[] = [];

          this.convert().from(originUnit).possibilities().forEach(poss => {
            items.push({
              origin: originUnit,
              target: poss,
              name: `${poss} (${this.convert().describe(poss).plural})`
            });
          });
          this.groupedDeviceMeasurementUnit.push({
            measure: measurement.name,
            label: originUnit,
            value: originUnit,
            items: items
          });
          this.selectedDeviceMeasurementsUnit[measurement.name] = {
            origin: originUnit,
            target: originUnit,
            name: `${originUnit} (${this.convert().describe(originUnit).plural})`
          };
        }
      }
    });
  }

  searchMeasures(): void {
    this.loadingMeasurements = true;
    this.totalMeasures = 0;
    const totalMeasures = [];
    const totalTableMeasures = [];
    const promises = [];
    const suffixes = [];
    this.measures = null;

    const utcRange = this.timezoneService.timestampRangeFromCalendarData(this.rangeDates);

    if (this.selectedAssetMeasurements && this.selectedAssetMeasurements.length > 0) {
      this.assetMeasurements.forEach(measurement => {
        if (this.selectedAssetMeasurements.includes(measurement.uuid)) {
          this.selectedAssetMeasurementUnit = null;
          if (this.selectedAssetMeasurementsUnit[measurement.name] !== undefined) {
            const target = this.selectedAssetMeasurementsUnit[measurement.name].target;
            this.selectedAssetMeasurementUnit = {
              target: target,
              origin: this.selectedAssetMeasurementsUnit[measurement.name].origin,
              name: `${target} (${this.convert().describe(target).plural})`
            };
          }
          const unitTarget = ( this.selectedAssetMeasurementUnit && this.selectedAssetMeasurementUnit.origin === measurement.measurementType.unit ? this.selectedAssetMeasurementUnit.target : measurement.measurementType.unit ?? '');
          const unitOrigin = measurement.measurementType.unit;

          const suffix = ' ' + unitTarget;
          let axisIndex;
          if (!suffixes.includes(suffix)) {
            suffixes.push(suffix);
          }
          axisIndex = suffixes.indexOf(suffix);

          const promise = this.measuresService.searchAssetMeasures(this.asset.tenant.uuid, this.asset.uuid,
            measurement.uuid, utcRange.from, utcRange.until)
            .toPromise().then(response => {
              totalMeasures.push({
                name: measurement.name,
                data: this.measuresService.buildSensorMeasures(response.measures, unitOrigin, unitTarget),
                yAxis: axisIndex,
                tooltip: { valueSuffix: suffix }
              });
              response.measures.forEach(measure => {
                measure.measurementName = measurement.name;
                measure.unit = unitTarget;
                if (measure.unit !== measurement.measurementType.unit && !isNaN(parseFloat(measure.value))) {
                  measure.value =  this.measuresService.convertValue(parseFloat(measure.value), unitOrigin, unitTarget);
                }
                totalTableMeasures.push(measure);
              });
            });

          promises.push(promise);

        }
      });
    }

    if (this.selectedDeviceMeasurements && this.selectedDeviceMeasurements.length > 0) {
      this.deviceMeasurements.forEach(measurement => {
        if (this.selectedDeviceMeasurements.includes(measurement.uuid)) {
          this.selectedDeviceMeasurementUnit = null;
          if (this.selectedDeviceMeasurementsUnit[measurement.name] !== undefined) {
            const target = this.selectedDeviceMeasurementsUnit[measurement.name].target;
            this.selectedDeviceMeasurementUnit = {
              origin: measurement.unit,
              target: target,
              name: `${target} (${this.convert().describe(target as any).plural})`
            };
          }
          const unitTarget = ( this.selectedDeviceMeasurementUnit && this.selectedDeviceMeasurementUnit.origin === measurement.unit ? this.selectedDeviceMeasurementUnit.target : measurement.unit ?? '');
          const unitOrigin = measurement.unit;

          const suffix = ' ' + unitTarget;
          let axisIndex;
          if (!suffixes.includes(suffix)) {
            suffixes.push(suffix);
          }
          axisIndex = suffixes.indexOf(suffix);

          const promise = this.measuresService.searchDeviceMeasures(this.asset.tenant.uuid, measurement.deviceUuid,
            measurement.uuid, utcRange.from, utcRange.until)
            .toPromise().then(response => {
              totalMeasures.push({
                name: `${measurement.name} (${measurement.deviceSerialNumber})`,
                data: this.measuresService.buildSensorMeasures(response.measures, unitOrigin, unitTarget),
                yAxis: axisIndex,
                tooltip: { valueSuffix: suffix }
              });
              response.measures.forEach(measure => {
                measure.measurementName = `${measurement.name} (${measurement.deviceSerialNumber})`;
                measure.unit = unitTarget;
                if (measure.unit != measurement.measurementType.unit && !isNaN(parseFloat(measure.value))) {
                  measure.value =  this.measuresService.convertValue(parseFloat(measure.value), unitOrigin, unitTarget);
                }
                totalTableMeasures.push(measure);
              });
            });

          promises.push(promise);

        }
      });
    }

    Promise.all(promises).then(() => {
      this.measures = undefined;
      this.tableMeasures = undefined;
      this.totalMeasures = totalTableMeasures.length;
      this.graphAxis = suffixes.map(suffix => ( { labels: { format: '{value}' + suffix }, title: { enabled: false } } ) );
      setTimeout(() => this.tableMeasures = (totalTableMeasures.length > 0 ) ? totalTableMeasures : undefined, 0);
      setTimeout(() => this.measures = (totalMeasures.length > 0 ) ? totalMeasures : undefined, 0);
    }).finally(() => this.loadingMeasurements = false);
  }



  clearConversions(): void {
    this.selectedAssetMeasurementsUnit = [];
    this.selectedDeviceMeasurementsUnit = [];
    this.loadDeviceMeasurementUnits();
    this.loadAssetMeasurementUnits();
    this.searchMeasures();
    this.displayActionsDialog = false;
  }

}
